import {
  Audio,
  ExportFormat,
  isTranscriptionState,
  transcriptionStateFromError,
  useMutationExportTranscript,
  useMutationTranscribeAudio,
  useQueryTranscription,
} from "@apis:audio/audio";
import { LANGUAGE_AUTO_DETECT } from "@pages:app/audio/const";
import { computePeaks, wavDurationSeconds } from "@pages:app/audio/util";
import FileSaver from "file-saver";
import { get } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

/*
 * Where a transcription job stands, from the user's point of view.
 * `queued` covers a task that exists but no worker has picked up yet.
 */
export type TranscriptionPhase =
  | "idle"
  | "uploading"
  | "queued"
  | "processing"
  | "completed"
  | "failed";

const POLL_INTERVAL_MS = 2000;

const phaseOf = (
  taskId: string,
  isUploading: boolean,
  status: Audio["GET"]["State"]["status"] | undefined,
): TranscriptionPhase => {
  if (!taskId) return isUploading ? "uploading" : "idle";

  switch (status) {
    case "started":
      return "processing";
    case "completed":
      return "completed";
    case "failed":
      return "failed";
    default:
      return "queued";
  }
};

export interface Waveform {
  peaks: number[];
  durationSeconds: number;
}

/*
 * Decodes the recording in the browser to draw its shape and know its length.
 * When the browser cannot decode the format, the length is still read from the WAV header
 * where possible and the waveform stays empty.
 */
export const useWaveform = (file: File | null): Waveform | null => {
  const [waveform, setWaveform] = useState<Waveform | null>(null);

  useEffect(() => {
    setWaveform(null);
    if (!file) return;

    let cancelled = false;
    const context = new AudioContext();

    file
      .arrayBuffer()
      .then(async (bytes) => {
        try {
          const buffer = await context.decodeAudioData(bytes.slice(0));
          return {
            peaks: computePeaks(buffer),
            durationSeconds: buffer.duration,
          };
        } catch (decodeError) {
          console.warn("Could not decode audio for the waveform", decodeError);
          const durationSeconds = wavDurationSeconds(bytes);
          return durationSeconds ? { peaks: [], durationSeconds } : null;
        }
      })
      .then((decoded) => {
        if (!cancelled) setWaveform(decoded);
      })
      .catch(() => {
        if (!cancelled) setWaveform(null);
      })
      .finally(() => {
        void context.close();
      });

    return () => {
      cancelled = true;
    };
  }, [file]);

  return waveform;
};

/*
 * Owns one transcription job end to end: the form values, the upload, the polling,
 * and the result, which is the most refined stage the bot produced
 * (diarization, else alignment, else transcription), as the API answers by default.
 */
export const useTranscriptionJob = (botId: string | undefined) => {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [language, setLanguage] = useState<string>(LANGUAGE_AUTO_DETECT);
  const [numSpeakers, setNumSpeakers] = useState<number | null>(null);
  const [taskId, setTaskId] = useState<string>("");
  const [taskStatus, setTaskStatus] = useState<Audio["GET"]["State"]>();
  const [result, setResult] = useState<Audio["GET"]["Response"]>();
  const [isPolling, setIsPolling] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string>("");
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const waveform = useWaveform(file);

  const transcribeAudioMutation = useMutationTranscribeAudio(botId as string, {
    onSuccess: (data) => {
      setTaskId(data.id);
      setIsPolling(true);
      setIsUploading(false);
    },
    onError: (mutationError) => {
      setError(
        get(
          mutationError,
          "response.data.detail",
          t("audio.upload.errors.uploadFailed"),
        ),
      );
      setIsUploading(false);
    },
  });

  const { refetch: pollTranscription } = useQueryTranscription(
    taskId,
    undefined,
    { enabled: false, throwOnError: false },
  );

  const selectFile = (selectedFile: File) => {
    const contentType = selectedFile.type.split(";", 1)[0].trim().toLowerCase();
    if (contentType.startsWith("audio/") || contentType === "video/mp4") {
      setFile(selectedFile);
      setError("");
    } else {
      setError(t("audio.upload.errors.invalidAudioFile"));
    }
  };

  const clearFile = () => {
    setFile(null);
    setError("");
  };

  /*
   * Sends the selected file. Also used to retry a failed job with the same settings,
   * so any previous job state is cleared first.
   */
  const transcribe = async () => {
    if (!file || !language) {
      setError(t("audio.upload.errors.selectFileAndLanguage"));
      return;
    }

    setTaskId("");
    setTaskStatus(undefined);
    setResult(undefined);
    setIsUploading(true);
    setStartedAt(Date.now());
    setError("");

    await transcribeAudioMutation.mutateAsync({
      file,
      languageCode:
        language === LANGUAGE_AUTO_DETECT
          ? undefined
          : (language as Audio["POST"]["RequestQuery"]["languageCode"]),
      numSpeakers: numSpeakers || undefined,
    });
  };

  // Polling for task status
  useEffect(() => {
    if (!taskId || !isPolling) return;

    const pollInterval = setInterval(async () => {
      let data: Audio["GET"]["Response"] | undefined;
      let pollError: unknown;
      try {
        ({ data, error: pollError } = await pollTranscription());
      } catch (thrown) {
        // A failed transcription returns 424
        pollError = thrown;
      }

      // A failed transcription returns 424.
      // The query keeps the last good body in `data`.
      if (pollError) {
        const failed = transcriptionStateFromError(pollError);
        setIsPolling(false);
        if (failed) {
          setTaskStatus(failed);
        } else {
          setError(t("audio.upload.errors.statusCheckFailed"));
        }
        return;
      }

      // Until it completes the transcription answers with its state
      if (isTranscriptionState(data)) {
        setTaskStatus(data);
        return;
      }

      if (!data) {
        setError(t("audio.upload.errors.statusCheckFailed"));
        setIsPolling(false);
        return;
      }

      // Anything else is the result, so the transcription is done.
      setIsPolling(false);
      setResult(data);
      setTaskStatus({ status: "completed" });
    }, POLL_INTERVAL_MS);

    return () => clearInterval(pollInterval);
  }, [taskId, isPolling, pollTranscription, t]);

  const reset = () => {
    setFile(null);
    setLanguage(LANGUAGE_AUTO_DETECT);
    setTaskId("");
    setTaskStatus(undefined);
    setResult(undefined);
    setStartedAt(null);
    setError("");
  };

  return {
    phase: phaseOf(taskId, isUploading, taskStatus?.status),
    file,
    waveform,
    language,
    setLanguage,
    numSpeakers,
    setNumSpeakers,
    taskId,
    taskStatus,
    result,
    isUploading,
    startedAt,
    error,
    selectFile,
    clearFile,
    transcribe,
    reset,
  };
};

/*
 * Seconds elapsed since `startedAt`, increasing every second while it is set.
 */
export const useElapsedSeconds = (startedAt: number | null): number => {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!startedAt) {
      setElapsed(0);
      return;
    }

    const tick = () => setElapsed((Date.now() - startedAt) / 1000);
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [startedAt]);

  return elapsed;
};

export type TranscriptionJob = ReturnType<typeof useTranscriptionJob>;

/*
 * Exports the transcript, with its summary if there is one, as Word or PDF.
 */
export const useTranscriptExport = (
  botId: string,
  title: string,
  transcript: string,
  summary?: string,
) => {
  const { t } = useTranslation();
  const exportDocx = useMutationExportTranscript(botId, "docx");
  const exportPdf = useMutationExportTranscript(botId, "pdf");
  const [error, setError] = useState<string>("");

  const exportAs = (format: ExportFormat) => {
    const mutation = format === "pdf" ? exportPdf : exportDocx;
    const label = format === "pdf" ? "PDF" : "Word";
    setError("");

    mutation.mutate(
      { transcript, summary: summary || undefined, title },
      {
        onSuccess: (blob) => FileSaver.saveAs(blob, `${title}.${format}`),
        onError: () => setError(t("audio.export.failed", { format: label })),
      },
    );
  };

  return {
    exportAs,
    isExporting: exportDocx.isPending || exportPdf.isPending,
    error,
  };
};

export interface AudioPlayer {
  ready: boolean;
  unplayable: boolean;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  play: () => void;
  pause: () => void;
  toggle: () => void;
  seek: (seconds: number) => void;
}

const TICKS_PER_SECOND = 10;

/*
 * Plays the chosen recording from memory. The element is never in the DOM;
 * the page draws its own controls and the transcript follows `currentTime`.
 */
export const useAudioPlayer = (file: File | null): AudioPlayer => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [ready, setReady] = useState(false);
  const [unplayable, setUnplayable] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    setReady(false);
    setUnplayable(false);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    if (!file) {
      audioRef.current = null;
      return;
    }

    const url = URL.createObjectURL(file);
    const audio = document.createElement("audio");
    audio.preload = "metadata";
    audio.src = url;
    audioRef.current = audio;

    const onMetadata = () => {
      setDuration(Number.isFinite(audio.duration) ? audio.duration : 0);
      setReady(true);
    };
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnded = () => {
      setIsPlaying(false);
      setCurrentTime(Number.isFinite(audio.duration) ? audio.duration : 0);
    };
    const onError = () => {
      setReady(false);
      setUnplayable(true);
    };

    audio.addEventListener("loadedmetadata", onMetadata);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("error", onError);

    return () => {
      audio.pause();
      audio.removeEventListener("loadedmetadata", onMetadata);
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("error", onError);
      audioRef.current = null;
      URL.revokeObjectURL(url);
    };
  }, [file]);

  useEffect(() => {
    if (!isPlaying) return;

    let frame = 0;
    let lastTick = -1;
    const follow = () => {
      const audio = audioRef.current;
      if (audio) {
        const tick = Math.floor(audio.currentTime * TICKS_PER_SECOND);
        if (tick !== lastTick) {
          lastTick = tick;
          setCurrentTime(audio.currentTime);
        }
      }
      frame = requestAnimationFrame(follow);
    };
    frame = requestAnimationFrame(follow);

    return () => cancelAnimationFrame(frame);
  }, [isPlaying]);

  const play = useCallback(() => {
    void audioRef.current?.play().catch(() => {
      setReady(false);
      setUnplayable(true);
    });
  }, []);

  const pause = useCallback(() => {
    audioRef.current?.pause();
  }, []);

  const toggle = useCallback(() => {
    if (audioRef.current?.paused) play();
    else pause();
  }, [play, pause]);

  const seek = useCallback((seconds: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    const limit = Number.isFinite(audio.duration) ? audio.duration : seconds;
    const target = Math.max(0, Math.min(seconds, limit));
    audio.currentTime = target;
    setCurrentTime(target);
  }, []);

  return useMemo(
    () => ({
      ready,
      unplayable,
      isPlaying,
      currentTime,
      duration,
      play,
      pause,
      toggle,
      seek,
    }),
    [
      ready,
      unplayable,
      isPlaying,
      currentTime,
      duration,
      play,
      pause,
      toggle,
      seek,
    ],
  );
};

export type RecorderStatus = "idle" | "requesting" | "recording" | "stopping";

export interface MicrophoneRecorder {
  supported: boolean;
  status: RecorderStatus;
  levels: number[];
  startedAt: number | null;
  error: string;
  start: () => Promise<void>;
  stop: () => Promise<File | null>;
  cancel: () => void;
}

const RECORDER_MIME_TYPES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
  "audio/ogg;codecs=opus",
];
const LEVEL_INTERVAL_MS = 100;
const LEVEL_BUCKETS = 160;
const LEVEL_GAIN = 4;

const extensionFor = (mimeType: string) => {
  if (mimeType.includes("mp4")) return "m4a";
  if (mimeType.includes("ogg")) return "ogg";
  return "webm";
};

const recordingName = (mimeType: string) => {
  const stamp = new Date()
    .toISOString()
    .slice(0, 16)
    .replace("T", "-")
    .replace(":", "");
  return `recording-${stamp}.${extensionFor(mimeType)}`;
};

/*
 * Records from the microphone into a File that goes through the normal upload.
 * While recording, `levels` holds the loudness of the whole take so far in at most 160 buckets.
 * When they are full, neighbours are merged so the strip keeps growing.
 */
export const useMicrophoneRecorder = (): MicrophoneRecorder => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<RecorderStatus>("idle");
  const [levels, setLevels] = useState<number[]>([]);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [error, setError] = useState("");
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const contextRef = useRef<AudioContext | null>(null);
  const timerRef = useRef<number | null>(null);
  const bucketMsRef = useRef(LEVEL_INTERVAL_MS);
  const pendingRef = useRef<{ sum: number; count: number; since: number }>({
    sum: 0,
    count: 0,
    since: 0,
  });

  const supported =
    typeof MediaRecorder !== "undefined" &&
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia;

  const release = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    void contextRef.current?.close();
    contextRef.current = null;
    recorderRef.current = null;
  }, []);

  useEffect(() => release, [release]);

  const start = useCallback(async () => {
    if (!supported || status !== "idle") return;
    setError("");
    setStatus("requesting");

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });
    } catch (mediaError) {
      const name = (mediaError as DOMException | undefined)?.name;
      setError(
        name === "NotAllowedError" || name === "SecurityError"
          ? t("audio.record.blocked")
          : t("audio.record.failed"),
      );
      setStatus("idle");
      return;
    }

    const mimeType = RECORDER_MIME_TYPES.find((type) =>
      MediaRecorder.isTypeSupported(type),
    );
    const recorder = new MediaRecorder(
      stream,
      mimeType ? { mimeType } : undefined,
    );
    recorderRef.current = recorder;
    streamRef.current = stream;
    chunksRef.current = [];
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunksRef.current.push(event.data);
    };
    recorder.start(1000);

    const context = new AudioContext();
    contextRef.current = context;
    const analyser = context.createAnalyser();
    analyser.fftSize = 2048;
    context.createMediaStreamSource(stream).connect(analyser);
    const samples = new Float32Array(analyser.fftSize);

    bucketMsRef.current = LEVEL_INTERVAL_MS;
    pendingRef.current = { sum: 0, count: 0, since: Date.now() };
    setLevels([]);
    setStartedAt(Date.now());
    setStatus("recording");

    timerRef.current = window.setInterval(() => {
      analyser.getFloatTimeDomainData(samples);
      let energy = 0;
      for (const sample of samples) energy += sample * sample;
      const level = Math.min(
        1,
        Math.sqrt(energy / samples.length) * LEVEL_GAIN,
      );

      const pending = pendingRef.current;
      pending.sum += level;
      pending.count += 1;
      if (Date.now() - pending.since < bucketMsRef.current) return;

      const bucket = pending.sum / pending.count;
      pendingRef.current = { sum: 0, count: 0, since: Date.now() };
      setLevels((previous) => {
        if (previous.length < LEVEL_BUCKETS) return [...previous, bucket];
        bucketMsRef.current *= 2;
        const merged: number[] = [];
        for (let i = 0; i < previous.length; i += 2) {
          merged.push(Math.max(previous[i], previous[i + 1] ?? 0));
        }
        return [...merged, bucket];
      });
    }, LEVEL_INTERVAL_MS);
  }, [supported, status, t]);

  const finish = useCallback(
    (keep: boolean) =>
      new Promise<File | null>((resolve) => {
        const recorder = recorderRef.current;
        if (!recorder || recorder.state === "inactive") {
          release();
          setStatus("idle");
          resolve(null);
          return;
        }
        setStatus("stopping");
        recorder.onstop = () => {
          const chunks = chunksRef.current;
          const mimeType = recorder.mimeType || "audio/webm";
          release();
          setStatus("idle");
          setStartedAt(null);
          if (!keep || chunks.length === 0) {
            resolve(null);
            return;
          }
          const type = mimeType.split(";")[0];
          resolve(
            new File([new Blob(chunks, { type })], recordingName(type), {
              type,
            }),
          );
        };
        recorder.stop();
      }),
    [release],
  );

  const stop = useCallback(() => finish(true), [finish]);
  const cancel = useCallback(() => {
    void finish(false);
  }, [finish]);

  return { supported, status, levels, startedAt, error, start, stop, cancel };
};
